import axios from 'axios'
import type {
  AuthResponse,
  GoogleLoginRequest,
  User,
  DashboardStats,
  KYCStatusResponse,
  VendorKYC,
  PersonalInfoData,
  BusinessInfoData,
  BankingInfoData,
  TermsData,
} from '../types'
import { storage, redirect } from '../utils/storage'

// Detect environment (web vs React Native)
const isReactNative = typeof navigator !== 'undefined' && navigator.product === 'ReactNative'

// Get API URL based on environment
function getApiUrl(): string {
  if (isReactNative) {
    // React Native: use process.env or default
    return process.env.EXPO_PUBLIC_API_URL || 'http://localhost:8000'
  } else {
    // Web: Check for Vite env variable (import.meta.env.VITE_API_URL)
    // @ts-ignore - import.meta only exists in web bundlers
    try {
      // @ts-ignore
      if (import.meta?.env?.VITE_API_URL) {
        // @ts-ignore
        return import.meta.env.VITE_API_URL
      }
    } catch (e) {
      // import.meta not available (e.g., in tests or non-ESM environments)
    }

    // Fallback for development
    return 'http://localhost:8000'
  }
}

const API_URL = getApiUrl()

export const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token and language
apiClient.interceptors.request.use(
  config => {
    const token = storage.getItem('access_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // Add current language to requests
    const language = storage.getItem('language') || 'es'
    config.headers['Accept-Language'] = language

    return config
  },
  error => Promise.reject(error)
)

// Response interceptor to handle token refresh
apiClient.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      try {
        const refreshToken = storage.getItem('refresh_token')
        if (refreshToken) {
          const response = await axios.post(`${API_URL}/auth/token/refresh/`, {
            refresh: refreshToken,
          })

          const { access } = response.data
          storage.setItem('access_token', access)

          originalRequest.headers.Authorization = `Bearer ${access}`
          return apiClient(originalRequest)
        }
      } catch (refreshError) {
        // Refresh failed, clear tokens and redirect to login
        storage.removeItem('access_token')
        storage.removeItem('refresh_token')
        redirect('/login')
        return Promise.reject(refreshError)
      }
    }

    return Promise.reject(error)
  }
)

// Auth API endpoints
export const authApi = {
  googleLogin: async (accessToken: string): Promise<AuthResponse> => {
    const payload: GoogleLoginRequest = { access_token: accessToken }
    const response = await apiClient.post<AuthResponse>('/auth/google/login/', payload)
    return response.data
  },

  staffLogin: async (username: string, password: string): Promise<AuthResponse> => {
    const response = await apiClient.post<AuthResponse>('/auth/staff/login/', {
      username,
      password
    })
    return response.data
  },

  emailLogin: async (email: string, password: string): Promise<AuthResponse> => {
    const response = await apiClient.post<{
      access: string
      refresh: string
      user: User
    }>('/auth/login/', { email, password })

    return {
      user: response.data.user,
      tokens: {
        access: response.data.access,
        refresh: response.data.refresh
      },
      is_new_user: false
    }
  },

  register: async (email: string, password1: string, password2: string): Promise<AuthResponse> => {
    const response = await apiClient.post<{
      access: string
      refresh: string
      user: User
    }>('/auth/registration/', { email, password1, password2 })

    return {
      user: response.data.user,
      tokens: {
        access: response.data.access,
        refresh: response.data.refresh
      },
      is_new_user: true
    }
  },

  getCurrentUser: async (): Promise<User> => {
    const response = await apiClient.get<User>('/auth/me/')
    return response.data
  },

  logout: async (): Promise<void> => {
    const refreshToken = storage.getItem('refresh_token')
    if (refreshToken) {
      await apiClient.post('/auth/logout/', { refresh: refreshToken })
    }
    storage.removeItem('access_token')
    storage.removeItem('refresh_token')
  },

  helloWorld: async (): Promise<{ message: string; authenticated: boolean; user: string | null }> => {
    const response = await apiClient.get('/auth/hello/')
    return response.data
  },
}

// Dashboard API endpoints
export const dashboardApi = {
  getStats: async (): Promise<DashboardStats> => {
    const response = await apiClient.get<DashboardStats>('/backoffice/dashboard/stats/')
    return response.data
  },
  getAdminStats: async (): Promise<any> => {
    const response = await apiClient.get('/auth/admin/stats/')
    return response.data
  },
  getAllVendors: async (): Promise<any[]> => {
    const response = await apiClient.get('/auth/admin/vendors/')
    return response.data
  },
}

// Products API endpoints
export const productsApi = {
  // Public endpoints (for customers browsing)
  getAll: async (params?: {
    search?: string
    category?: number
  }): Promise<{count: number, results: any[]}> => {
    const response = await apiClient.get<{count: number, results: any[]}>('/products/public/', { params })
    return response.data
  },

  getById: async (id: number): Promise<any> => {
    const response = await apiClient.get<any>(`/products/public/${id}/`)
    return response.data
  },

  // Shortcut methods that delegate to vendor endpoints
  create: async (product: any): Promise<any> => {
    const response = await apiClient.post<any>('/products/vendor/', product)
    return response.data
  },

  update: async (id: number, product: any): Promise<any> => {
    const response = await apiClient.patch<any>(`/products/vendor/${id}/`, product)
    return response.data
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/products/vendor/${id}/`)
  },

  // Vendor endpoints (for sellers managing their products)
  vendor: {
    getAll: async (params?: {
      search?: string
      category?: number
      is_active?: boolean
    }): Promise<{count: number, results: any[]}> => {
      const response = await apiClient.get<{count: number, results: any[]}>('/products/vendor/', { params })
      return response.data
    },

    getById: async (id: number): Promise<any> => {
      const response = await apiClient.get<any>(`/products/vendor/${id}/`)
      return response.data
    },

    create: async (product: any): Promise<any> => {
      const response = await apiClient.post<any>('/products/vendor/', product)
      return response.data
    },

    update: async (id: number, product: any): Promise<any> => {
      const response = await apiClient.patch<any>(`/products/vendor/${id}/`, product)
      return response.data
    },

    delete: async (id: number): Promise<void> => {
      await apiClient.delete(`/products/vendor/${id}/`)
    },

    uploadImage: async (file: File): Promise<{ url: string; filename: string }> => {
      const formData = new FormData()
      formData.append('image', file)
      const response = await apiClient.post('/products/vendor/upload-image/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      return response.data
    },

    uploadVideo: async (file: File): Promise<{ url: string; filename: string }> => {
      const formData = new FormData()
      formData.append('video', file)
      const response = await apiClient.post('/products/vendor/upload-video/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      return response.data
    },
  },

  // Admin endpoints (for admins managing all products)
  admin: {
    getAll: async (): Promise<any[]> => {
      const response = await apiClient.get('/products/admin/all/')
      return response.data
    },

    getById: async (id: number): Promise<any> => {
      const response = await apiClient.get(`/products/admin/${id}/`)
      return response.data
    },

    update: async (id: number, product: any): Promise<any> => {
      const response = await apiClient.patch(`/products/admin/${id}/update/`, product)
      return response.data
    },

    delete: async (id: number): Promise<void> => {
      await apiClient.delete(`/products/admin/${id}/delete/`)
    },
  },
}

// Orders API endpoints
export const ordersApi = {
  // Vendor endpoints (for sellers viewing their orders)
  vendor: {
    getMyOrders: async (): Promise<any[]> => {
      const response = await apiClient.get('/orders/vendor/my_orders/')
      return response.data
    },

    getById: async (id: number): Promise<any> => {
      const response = await apiClient.get(`/orders/vendor/${id}/`)
      return response.data
    },
  },

  // Admin endpoints (for admins managing all orders)
  admin: {
    getAll: async (): Promise<any[]> => {
      const response = await apiClient.get('/orders/admin/all/')
      return response.data
    },

    getById: async (id: number): Promise<any> => {
      const response = await apiClient.get(`/orders/admin/${id}/`)
      return response.data
    },

    updateStatus: async (id: number, status: string): Promise<any> => {
      const response = await apiClient.patch(`/orders/admin/${id}/status/`, { status })
      return response.data
    },
  },
}

// Categories API endpoints
export const categoriesApi = {
  // Public endpoints (for browsing)
  getAll: async (): Promise<{count: number, results: any[]}> => {
    const response = await apiClient.get<{count: number, results: any[]}>('/categories/')
    return response.data
  },

  getById: async (id: number): Promise<any> => {
    const response = await apiClient.get<any>(`/categories/${id}/`)
    return response.data
  },

  // Vendor endpoints (for sellers managing categories - uses public endpoints)
  create: async (category: { name: string; description?: string; parent_id?: number | null; image_url?: string }): Promise<any> => {
    const response = await apiClient.post('/categories/', category)
    return response.data
  },

  update: async (id: number, category: any): Promise<any> => {
    const response = await apiClient.patch(`/categories/${id}/`, category)
    return response.data
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/categories/${id}/`)
  },

  // Admin endpoints
  admin: {
    getAll: async (): Promise<any[]> => {
      const response = await apiClient.get('/categories/admin/all/')
      return response.data
    },

    getById: async (id: number): Promise<any> => {
      const response = await apiClient.get(`/categories/admin/${id}/`)
      return response.data
    },

    create: async (category: { name: string; description?: string; parent_id?: number | null; image_url?: string; is_active?: boolean }): Promise<any> => {
      const response = await apiClient.post('/categories/admin/create/', category)
      return response.data
    },

    update: async (id: number, category: any): Promise<any> => {
      const response = await apiClient.patch(`/categories/admin/${id}/update/`, category)
      return response.data
    },

    delete: async (id: number): Promise<void> => {
      await apiClient.delete(`/categories/admin/${id}/delete/`)
    },
  },
}

// KYC API endpoints
export const kycApi = {
  checkStatus: async (): Promise<KYCStatusResponse> => {
    const response = await apiClient.get<KYCStatusResponse>('/kyc/status/')
    return response.data
  },

  getMyKYC: async (): Promise<VendorKYC> => {
    const response = await apiClient.get<VendorKYC>('/kyc/vendor/my_kyc/')
    return response.data
  },

  submitStep1: async (data: PersonalInfoData): Promise<{ message: string; kyc: VendorKYC }> => {
    const response = await apiClient.post('/kyc/vendor/step1_personal_info/', data)
    return response.data
  },

  submitStep2: async (data: FormData): Promise<{ message: string; kyc: VendorKYC }> => {
    const response = await apiClient.post('/kyc/vendor/step2_document_info/', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },

  submitStep3: async (data: FormData): Promise<{ message: string; kyc: VendorKYC }> => {
    const response = await apiClient.post('/kyc/vendor/step3_address_info/', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },

  submitStep4: async (data: BusinessInfoData): Promise<{ message: string; kyc: VendorKYC }> => {
    const response = await apiClient.post('/kyc/vendor/step4_business_info/', data)
    return response.data
  },

  submitStep5: async (data: BankingInfoData): Promise<{ message: string; kyc: VendorKYC }> => {
    const response = await apiClient.post('/kyc/vendor/step5_banking_info/', data)
    return response.data
  },

  submitStep6: async (data: TermsData): Promise<{ message: string; kyc: VendorKYC }> => {
    const response = await apiClient.post('/kyc/vendor/step6_terms/', data)
    return response.data
  },

  // Admin endpoints
  getAllPending: async (): Promise<VendorKYC[]> => {
    const response = await apiClient.get<VendorKYC[]>('/kyc/admin/pending/')
    return response.data
  },

  approveKYC: async (kycId: number): Promise<{ message: string }> => {
    const response = await apiClient.post(`/kyc/admin/${kycId}/approve/`)
    return response.data
  },

  rejectKYC: async (kycId: number, reason: string): Promise<{ message: string }> => {
    const response = await apiClient.post(`/kyc/admin/${kycId}/reject/`, { reason })
    return response.data
  },
}
